import { Signal } from '@angular/core';
import { PathKind, SchemaPath, SchemaPathRules, validate } from '@angular/forms/signals';
import { TimesheetAddressState } from '../state/address';
import { hasAlphanumeric } from './required-text.rule';
import { isDefined } from '../utilities/is-defined';

/** Options of the {@link addressRequired} rule. */
export interface AddressRequiredConfig {
    /** message reported when no address is set */
    message?: string;
}

/**
 * Whether an address carries enough to be sent — the slice-shape counterpart of
 * `TimesheetAddressModel.isValid`, which needs a class instance and so cannot run against form or
 * slice values.
 *
 * Street, city and postal code, all three, exactly as that model demands them. `addrText` is a note
 * beside a complete address, never an address on its own: an entry the backend receives with only a
 * description carries no place it could be booked against.
 *
 * Each of the three has to say something — see {@link hasAlphanumeric}. A field of spaces satisfies
 * the legacy `length > 0` test and the native `required` check alike, yet names no place, and an
 * address like that would reach the backend as blanks.
 *
 * @param address the address to check
 * @returns whether the address is usable
 */
export const isUsableAddress = (address: TimesheetAddressState | null): boolean => {
    if (!address) {
        return false;
    }
    return hasAlphanumeric(address.street) && hasAlphanumeric(address.city) && hasAlphanumeric(address.postCode);
};

/**
 * Signal-forms schema rule requiring an address.
 *
 * Whether an address is required at all is a configuration question — `displayTimesheetAddress`
 * gates it — so the caller passes that in as a signal rather than the rule reaching into the
 * store. With the flag off the rule is inert, matching the legacy `checkValidity`, which only
 * checked manual-entry addresses when the address column was shown.
 *
 * The rule only checks that the address is there, not what it holds: the address picker already
 * refuses an address that is not {@link isUsableAddress usable}, so checking its content again here
 * would validate the same address twice.
 *
 * The form field holds the address's id rather than the address itself, so the rule needs somewhere
 * to resolve it against — an id that resolves to nothing, e.g. an address deleted after the entry was
 * recorded, counts as no address. That lookup is passed in as a signal for the same reason `enabled`
 * is: the rule stays a pure function of its arguments, testable without a store, and the one place
 * that reads the address slice for this schema is the page that builds the lookup, not every rule
 * that happens to need an address.
 *
 * @param path the address path to validate
 * @param enabled whether an address is required, as a signal
 * @param addresses the addresses a stored id resolves against, keyed by id, as a signal
 * @param config optional message
 */
export function addressRequired<TPathKind extends PathKind = PathKind.Root>(
    path: SchemaPath<string | null, SchemaPathRules.Supported, TPathKind>,
    enabled: Signal<boolean>,
    addresses: Signal<ReadonlyMap<string, TimesheetAddressState>>,
    config?: AddressRequiredConfig,
): void {
    validate(path, (context) => {
        if (!enabled()) {
            return undefined;
        }
        const id = context.value();
        const resolved = id ? (addresses().get(id) ?? null) : null;
        return isDefined(resolved) ? undefined : { kind: 'addressRequired', message: config?.message ?? '' };
    });
}
